import jquery from 'jquery';
import EventBus from '@/event-bus';
import _reduce from 'lodash/reduce';
import _forEach from 'lodash/forEach';
import _uniq from 'lodash/uniq';
import _compact from 'lodash/compact';
import _map from 'lodash/map';
import _difference from 'lodash/difference';
import _size from 'lodash/size';
import _toArray from 'lodash/toArray';
import _toNumber from 'lodash/toNumber';
import _toInteger from 'lodash/toInteger';
import _clone from 'lodash/clone';
import _debounce from 'lodash/debounce';
import _isObject from 'lodash/isObject';
import _isArray from 'lodash/isArray';
import _concat from 'lodash/concat';
import _union from 'lodash/union';
import _pick from 'lodash/pick';

import Promise from 'promise';
import Product from '@/product';
import createStorage from '@/storage';
import _Singleton, { deferredSingletonExport } from '@/tools/singleton';
import isOnScreen from '@/tools/isOnScreen';
import sortedNumericIdsEqual from '@/helpers/sortedNumericIdsEqual';
import forEachInRafChunks from '@/helpers/forEachInRafChunks';
import perfDebug from '@/helpers/perfDebug';
import Template from '@/template';
import Storage from './storage';
import '@/products/recently-viewed';

const Store = createStorage({ name: 'common-reviews-products' });
const AWAITTIME = 40;

/**
 * Одна проходка NodeList → карта id товара → узлы формы (без повторного querySelectorAll).
 * @param {ArrayLike<Element>} nodes
 * @return {Object.<number, Element[]>}
 */
function buildFormsByProductIdMap(nodes) {
  return _reduce(
    nodes,
    function(acc, node) {
      const id = _toInteger(node.getAttribute('data-product-id'));
      if (!acc[id]) {
        acc[id] = [];
      }
      acc[id].push(node);

      return acc;
    },
    {},
  );
}

/**
 * Читает data-* с формы товара без jQuery.data (меньше парсинга и дублирования JSON).
 * @param {Element} form
 * @return {{ productId: number, productJson: object|null, withoutManyVariants: boolean, originalUpdatedAt: string|undefined }}
 */
function readProductFormDataset(form) {
  const ds = form.dataset;
  const $f = jquery(form);

  let productId = _toInteger(ds.productId);
  if (!productId) {
    productId = _toInteger($f.data('product-id'));
  }

  let productJson = null;
  if (ds.productJson) {
    try {
      productJson = JSON.parse(ds.productJson);
    } catch {
      productJson = null;
    }
  }
  if (productJson == null) {
    const jqJson = $f.data('product-json');
    if (jqJson && typeof jqJson === 'object') {
      productJson = jqJson;
    }
  }

  let withoutManyVariants =
    ds.productWithoutManyVariants === 'true' ||
    ds.productWithoutManyVariants === '1' ||
    ds.productWithoutManyVariants === '';
  if ($f.data('product-without-many-variants') === true) {
    withoutManyVariants = true;
  }

  let originalUpdatedAt;
  if (ds.productUpdatedAt != null && ds.productUpdatedAt !== '') {
    originalUpdatedAt = ds.productUpdatedAt;
  } else {
    const updatedAtFallback = $f.data('product-updated-at');
    if (updatedAtFallback != null) {
      originalUpdatedAt = updatedAtFallback;
    }
  }

  return {
    productId,
    productJson,
    withoutManyVariants,
    originalUpdatedAt,
  };
}

/** Сколько товаров инициализировать за один проход до уступки кадру (между requestAnimationFrame). */
const PRODUCTS_INIT_CHUNK = 4;

/** Сколько узлов [data-product-id] обрабатывать за кадр при скане DOM в _getDomId. */
const GET_DOM_ID_SCAN_CHUNK = 64;

/** Сколько первых товаров инициализировать по одному за кадр (прогрев JIT/шаблонов). */
const PRODUCTS_INIT_FIRST_BATCH_SIZE = 2;
/** Бюджет времени на чанк для первой пачки (мс wall-clock для уступки внутри чанка). */
const PRODUCTS_INIT_CHUNK_TIME_FIRST_MS = 15;

/** Встроенные id шаблонов OptionSelector — прогрев до первого товара снижает cold-start JIT. */
const OPTION_TEMPLATE_WARMUP_IDS = [
  'option-default',
  'option-select-image',
  'option-select',
  'option-radio',
  'option-span',
  'option-preview',
  'option-preview-text',
];

/**
 * Дешёвый вызов Template.render по каждому типу — компиляция lodash-шаблонов вне горячего пути ProductInstance.
 */
function warmupBuiltInOptionTemplates() {
  const stub = {
    option: {
      id: 0,
      handle: '__warmup__',
      title: '',
      selected: 0,
      values: [
        {
          id: 0,
          position: 0,
          title: '',
          disabled: false,
        },
      ],
    },
    initOption: false,
    classes: {
      option: 'option',
      label: 'option-label',
      values: 'option-values',
      value: 'option-value',
    },
  };

  _forEach(OPTION_TEMPLATE_WARMUP_IDS, function(templateId) {
    try {
      Template.render(stub, templateId);
    } catch {
      /* заглушка не подходит всем шаблонам — достаточно согреть те, что отработали */
    }
  });
}

let templatesWarmupDone = false;

/** Один раз синхронно до первого `Product` в `_initStoreProducts`. */
function warmupTemplatesIfNeeded() {
  if (templatesWarmupDone) {
    return;
  }
  templatesWarmupDone = true;
  warmupBuiltInOptionTemplates();
}

/**
 * @class
 *
 * @description
 * Централизованная работа с товарами. Производит связку с DOM через аттрибуты.
 * Триггерит все события в EventBus.
 *
 * @fires before:insales:product
 * @fires change_quantity:insales:product
 * @fires unchange_quantity:insales:product
 * @fires update_variant:insales:product
 * @fires always:insales:product
 */
const Products = function() {
  const self = this;
  const _perfCtorStart = perfDebug.isEnabled() ? perfDebug.now() : 0;

  // храним deferred обьекты для массовой отправки
  self.deferredList = [];

  // храним id товаров которых нет в json
  self.deleted = [];

  // настройки
  self.settings = {};

  // id товаров с вариантами на странице
  self.productIds = [];

  // Храним тут дебаунснутый метод для разовой отправки
  self._debouncedGetList = _debounce(function() {
    if (window.requestAnimationFrame) {
      window.requestAnimationFrame(() => self._getList());
    } else {
      self._getList();
    }
  }, AWAITTIME);

  // объект с готовыми к употреблению товарами
  self._products = {};

  // массив с актуальными id (которые есть на странице +-)
  self._storage = new Storage(self);

  EventBus.subscribe('products-storage-deleted:insales:common', function(deletedIds) {
    self.deleted = _compact(_uniq(_concat(self.deleted, deletedIds)));
  });

  // Инициализация: id из DOM → очередь запросов → события готовности
  self._getDomId()
    .done(function(domIds) {
      perfDebug.log('products:_getDomId:resolved', {
        visibleIds: domIds.visibleIds.length,
        hiddenIds: domIds.hiddenIds.length,
        onlyFirstVariantIds: domIds.onlyFirstVariantIds.length,
      });
      EventBus.publish('products-get-dom-id:insales:common', null);
      self._scheduleHiddenProductIdsLoad(domIds.hiddenIds);
      self._bootstrapVisibleAndFirstVariantLoads(domIds);
    })
    .fail(function() {
      self._publishProductsReady();
    });

  self._bindDynamycProducts();

  if (perfDebug.isEnabled()) {
    perfDebug.log('Products:constructor:sync', { after: '_bindDynamycProducts' }, perfDebug.now() - _perfCtorStart);
  }
};

/**
 * Страница дождалась основных пачек товаров (видимые + onlyFirstVariant); виджеты могут опираться на это событие.
 *
 * @private
 */
Products.prototype._publishProductsReady = function() {
  EventBus.publish('products-ready:insales:common', null);
};

/**
 * После DOM: сначала видимые id с полными вариантами, затем пачка «только первый вариант».
 * Оба шага при успехе или провале первого ведут ко второму; готовность страницы — после второго.
 *
 * @param {{ visibleIds: number[], onlyFirstVariantIds: number[] }} domIds
 * @private
 */
Products.prototype._bootstrapVisibleAndFirstVariantLoads = function(domIds) {
  const self = this;

  perfDebug.log('products:bootstrapLoads:start', {
    visibleIds: domIds.visibleIds.length,
    onlyFirstVariantIds: domIds.onlyFirstVariantIds.length,
  });

  const runFirstVariantBatch = function() {
    self._enqueueProductFetch(domIds.onlyFirstVariantIds, { withoutManyVariants: true })
      .done(function() {
        self._publishProductsReady();
      })
      .fail(function() {
        self._publishProductsReady();
      });
  };

  self._enqueueProductFetch(domIds.visibleIds, { immediate: true })
    .done(runFirstVariantBatch)
    .fail(runFirstVariantBatch);
};

/**
 * Карточки вне экрана (hidden): не тянем сразу, чтобы не конкурировать с первым рендером.
 * Один раз — по window.load или через 2s, что наступит раньше.
 *
 * @param {number[]} hiddenIds
 * @private
 */
Products.prototype._scheduleHiddenProductIdsLoad = function(hiddenIds) {
  const self = this;

  perfDebug.log('products:scheduleHiddenLoads', { hiddenIds: hiddenIds.length });

  if (!hiddenIds.length) {
    return;
  }

  let hiddenProductsIsLoaded = false;
  const loadHidden = function() {
    if (!hiddenProductsIsLoaded) {
      hiddenProductsIsLoaded = true;
      self._enqueueProductFetch(hiddenIds);
    }
  };

  if (document.readyState === 'complete') {
    loadHidden();
  } else {
    setTimeout(loadHidden, 2000);
    window.addEventListener('load', loadHidden);
  }
};

/**
 * Получаем готовый к употреблению товар
 * @method
 *
 * @param {number} productId - id товара
 *
 * @return {jQuery.promise} промис с полученным товаром
 *
 * @example
 * Products.get(123456)
 *    .done(function (onDone) { console.log('onDone', onDone) })
 *    .fail(function (onFail) { console.log('onFail', onFail) });
 */
Products.prototype.get = function(productId) {
  const self = this;

  return self._getOne(_toInteger(productId));
};

/**
 * @method
 * @description
 * Получение списка товаров
 *
 * @param {Array} idList - массив, состоящий из id товаров
 *
 * @return {jQuery.promise} информация по списку товаров
 *
 * @example
 * Products.getList([123456, 123455, 1234454, 123458])
 *    .done(function (onDone) { console.log('onDone', onDone) })
 *    .fail(function (onFail) { console.log('onFail', onFail) });
 */
Products.prototype.getList = function(_idList, cartIdsMap) {
  const self = this;
  const idList = _compact(_union(_uniq(_map(_idList, function(id) {
    return _toNumber(id);
  }))));

  const result = jquery.Deferred(); // eslint-disable-line new-cap

  // Кладём список товаров в options, чтобы запрашивать только их, а не вообще все товары
  const options = {
    idList,
    cartIdsMap
  };

  self._enqueueProductFetch(idList, options)
    .done(function(products) {
      const resolveIds = _map(products, 'id');
      // Сравниваем множества id; порядок элементов в массивах не важен (см. sortedNumericIdsEqual).
      if (sortedNumericIdsEqual(resolveIds, idList)) {
        result.resolve(products);
      } else {
        self._enqueueProductFetch(idList, options)
          .done(function(productsDouble) {
            result.resolve(productsDouble);
          })
          .fail(function() {
            result.resolve(products);
          });
      }
    })
    .fail(function() {
      result.resolve({});
    });

  return result.promise();
};

/**
 * @method
 * @description
 * Получение ранее просмотренных товаров
 *
 * @return {jQuery.promise} id ранее просмотренных товаров
 *
 */
Products.prototype.getRecentlyViewed = function() {
  return new Promise(function(resolve, reject) {
    Store.getItem('common-reviews-products')
      .then(function(recentlyViewed) {
        resolve(recentlyViewed);
      })
      .catch(function(err) {
        reject(err);
      });
  });
};

/**
 * Нужен ли вызов _getList в следующем кадре, без дебаунса страничных id.
 *
 * @param {Object} options
 * @return {boolean}
 * @private
 */
Products.prototype._shouldRunGetListImmediately = function(options) {
  return Boolean(
    options.immediate ||
    options.idList ||
    options.withoutManyVariants ||
    (options.cartIdsMap && options.cartIdsMap.size)
  );
};

/**
 * Планирует один проход _getList (rAF или сразу, либо дебаунс для «обычных» страничных товаров).
 *
 * @param {Object} options
 * @private
 */
Products.prototype._scheduleGetListRun = function(options) {
  const self = this;

  if (self._shouldRunGetListImmediately(options)) {
    if (options.immediate && perfDebug.isEnabled()) {
      perfDebug.log('products:getList:firstBatch:immediate', {
        immediate: true,
      });
    }
    if (window.requestAnimationFrame) {
      window.requestAnimationFrame(() => self._getList(options));
    } else {
      self._getList(options);
    }
  } else {
    self._debouncedGetList();
  }
};

/**
 * @method
 * @private
 *
 * @description
 * Добавляет батч в очередь отложенных промисов и планирует общий вызов _getList.
 * Точка входа из инита страницы, getList() и get().
 *
 * @param {Array} idList - массив id товаров
 * @param {Object} [options]
 * @param {boolean} [options.withoutManyVariants] — только первый вариант на товар
 * @return {jQuery.promise} промис получения информации о списке товаров
 */
Products.prototype._enqueueProductFetch = function(idList, options = {}) {
  const self = this;
  const result = jquery.Deferred(); // eslint-disable-line new-cap
  const idListArray = (_isArray(idList)) ? _toArray(idList) : _toArray([ idList ]);

  if (!_compact(idListArray).length) {
    result.reject();

    return result.promise();
  }

  if (options.withoutManyVariants) {
    idListArray.forEach(id => {
      self.uniqueIdSetWithoutManyVariants.add(id);
    });
  }

  if (!options.withoutManyVariants) {
    self.productIds = _compact(
      _union(
        _uniq(
          _map(_concat(self.productIds, idListArray), function(id) {
            return _toNumber(id);
          })
        )
      )
    );
  }

  perfDebug.log('products:enqueueFetch', {
    batchSize: idListArray.length,
    withoutManyVariants: Boolean(options.withoutManyVariants),
    hasExplicitIdList: Boolean(options.idList && options.idList.length),
    debouncedPath: !self._shouldRunGetListImmediately(options),
  });

  // id батча на deferred — по ним потом резолвим этот промис из _resolveBatchDeferred
  result.idList = idListArray;
  // Очередь ожидающих getList/get: заполняется при каждом _enqueueProductFetch, снимается в _processDeferredListAfterLoad
  self.deferredList.push(result);

  /**
   * Раньше debounce здесь был супер решением, потому что всегда была одна пачка с id товаров в self.productIds. Сейчас мы разделяем запросы на 3 группы:
   * 1. Обычные товары с вариантами
   * 2. Товары получаемые вызовом getList (в т.ч. для корзины)
   * 3. Товары без вариантов
   * Для обычных товаров с вариантами дебаунсим _getList, чтобы он не выполнялся чаще раза в 40мс, потому что обычных товаров может быть 2 пачки (idList и idListHidden), а для всех остальных делаем обычный вызов _getList. Стараемся делать всё это в requestAnimationFrame.
   */
  self._scheduleGetListRun(options);

  return result.promise();
};

/**
 * Какие id уходят в один проход _getList / getProducts:
 * без вариантов — из uniqueIdSetWithoutManyVariants;
 * явный idList в options (getList, корзина и т.д.) — он;
 * иначе — накопленные страничные productIds.
 *
 * @method
 * @private
 *
 * @param {Object} options
 * @return {number[]}
 */
Products.prototype._productIdsForOptions = function(options) {
  const self = this;

  if (options.withoutManyVariants) {
    return _clone([ ...self.uniqueIdSetWithoutManyVariants ]);
  }
  if (options.idList && options.idList.length) {
    return _clone(options.idList);
  }

  return _clone(self.productIds);
};

/**
 * После удачной загрузки пачки товаров: резолвим те deferred из очереди, чьи id полностью покрыты ответом.
 * При повторном запросе (isRetry) услабляем условие: дотягиваем батчи, которые частично уже резолвились.
 *
 * @method
 * @private
 *
 * @param {Array<number>} resolveIds — id полученных товаров (как в ответе стора)
 * @param {Object} opts
 * @param {boolean} [opts.isRetry]
 */
/**
 * Снимает блокировку с отложенных get/getList после ошибки загрузки или инита:
 * резолвит все незакрытые батчи с тем, что уже есть в сторе (как при isRetry).
 *
 * @private
 */
Products.prototype._flushProductFetchWaiters = function() {
  this._processDeferredListAfterLoad([], { isRetry: true });
};

Products.prototype._processDeferredListAfterLoad = function(resolveIds, opts = {}) {
  const self = this;
  const isRetry = Boolean(opts.isRetry);

  _forEach(self.deferredList, function(deferred, index) {
    const isInited = self.testIds(deferred.idList, resolveIds);
    const shouldResolve = isRetry
      ? (!deferred.isResolved || isInited)
      : (!deferred.isResolved && isInited);

    if (shouldResolve) {
      self._resolveBatchDeferred(deferred);
      deferred.isResolved = true;
      if (isRetry && self.deferredList[index]) {
        self.deferredList[index].isResolved = true;
      }
    }
  });

  self.deferredList = self.deferredList.filter(deferred => !deferred.isResolved);
};

/**
 * @method
 * @private
 *
 * @param {Object[]} products
 * @param {number[]} productIds
 * @param {Object} options
 */
Products.prototype._applyLoadedProducts = function(products, productIds, options) {
  const self = this;
  const resolveIds = _map(products, 'id');

  perfDebug.log('products:applyLoaded', {
    resolveIds: resolveIds.length,
    requestedIds: productIds.length,
    fullMatch: sortedNumericIdsEqual(resolveIds, productIds),
  });

  if (sortedNumericIdsEqual(resolveIds, productIds)) {
    const _tInit = perfDebug.isEnabled() ? perfDebug.now() : 0;

    self._initStoreProducts(products)
      .then(function() {
        if (perfDebug.isEnabled()) {
          perfDebug.log('products:_initStoreProducts:rafChunksDone', {}, perfDebug.now() - _tInit);
        }
        self._processDeferredListAfterLoad(resolveIds, { isRetry: false });
      })
      .catch(function(err) {
        console.warn('Products: ошибка _initStoreProducts', err);
        self._flushProductFetchWaiters();
      });
  } else {
    // Не все id в ответе: товар мог быть удалён, скрыт и т.п. Повторяем getProducts и резолвим то, что удалось добрать.
    console.warn('Products._getList: Не все товары были получены. Повторяем запрос.', resolveIds, productIds);
    self._storage.getProducts(productIds, options)
      .then(function(retryProducts) {
        const resolveIdsRetry = _map(retryProducts, 'id');

        console.warn('Products._getList: Результат выполнения повторного запроса: ', resolveIdsRetry, productIds);

        self._initStoreProducts(retryProducts)
          .then(function() {
            self._processDeferredListAfterLoad(resolveIdsRetry, { isRetry: true });
          })
          .catch(function(err) {
            console.warn('Products: ошибка _initStoreProducts при повторе', err);
            self._flushProductFetchWaiters();
          });
      })
      .catch(function(err) {
        console.warn('Products: ошибка повторного getProducts', err);
        self._flushProductFetchWaiters();
      });
  }
};

/**
 * @method
 * @private
 *
 * @description
 * Забираем json из стора (или с бэка через storage), инитим Product / плейсхолдеры, затем резолвим отложенные промисы из deferredList.
 *
 * @return {void}
 */
Products.prototype._getList = function(options = {}) {
  const self = this;
  const productIds = self._productIdsForOptions(options);

  perfDebug.log('products:getList:callStorage', {
    productIds: productIds.length,
    withoutManyVariants: Boolean(options.withoutManyVariants),
    cartIdsMap: Boolean(options.cartIdsMap && options.cartIdsMap.size),
  });

  self._storage.getProducts(productIds, options)
    .then(function(products) {
      self._applyLoadedProducts(products, productIds, options);
    })
    .catch(function(err) {
      console.warn('Products: ошибка getProducts', err);
      self._flushProductFetchWaiters();
    });
};

/**
 * Резолвит один deferred из deferredList: объект { [id]: сериализованный товар } по id из batchDeferred.idList.
 * Данные берутся через _pickInitedProducts (и экземпляр Product, и сырой json из плейсхолдера).
 *
 * @method
 * @private
 * @param {jQuery.Deferred} batchDeferred — deferred с полем idList
 */
Products.prototype._resolveBatchDeferred = function(batchDeferred) {
  const self = this;
  const productIds = batchDeferred.idList;
  const initedProducts = self._pickInitedProducts(productIds);

  const serializedProducts = _reduce(productIds, function(result, productId) {
    if (initedProducts[productId]) {
      result[productId] = initedProducts[productId];
    }

    return result;
  }, {});

  batchDeferred.resolve(serializedProducts);
};

/**
 * Обновление настроек продуктов
 * @method
 *
 * @param {Object} settings - объект с применяемыми настройками.
 *
 * @return {void}
 */
Products.prototype.setConfig = function(settings) {
  const self = this;

  self.settings = settings;

  _forEach(self._products, function(product) {
    product.setConfig(self.settings);
  });
};

/**
 * @description
 * Скан DOM: элементы [data-product-id]. Формирует три списка id:
 * visibleIds — видимые или первые до лимита, к загрузке с бэка с полными вариантами;
 * hiddenIds — вне экрана (отложенная подгрузка);
 * onlyFirstVariantIds — только те, у кого в разметке without-many-variants и кто не пересекается с двумя первыми группами / JSON.
 *
 * @method
 * @private
 *
 * @return {jQuery.promise<{ visibleIds: number[], hiddenIds: number[], onlyFirstVariantIds: number[] }>}
 */
Products.prototype._getDomId = function() {
  const self = this;
  const _perfGetDomIdSync = perfDebug.isEnabled() ? perfDebug.now() : 0;
  const result = jquery.Deferred(); // eslint-disable-line new-cap
  const idSet = new Set();
  const idSetHidden = new Set();
  const idFromJSONSet = new Set();
  const idSetWithoutManyVariants = new Set();

  self.productsOriginalUpdatedAt = new Map();
  self.uniqueIdSetWithoutManyVariants = new Set();

  function runDomIdScan() {
    const _perfScanStart = perfDebug.isEnabled() ? perfDebug.now() : 0;

    // Карта форм для _initStoreProducts должна пересобираться после нового скана DOM
    self._formsByProductIdCache = null;

    if (perfDebug.isEnabled()) {
      perfDebug.mark('_getDomId:scan:start');
    }

    perfDebug.log('_getDomId:runDomIdScan:enter', { readyState: document.readyState });

    const forms = perfDebug.isEnabled()
      ? perfDebug.timeSync('_getDomId:querySelectorAll', function() {
        return document.querySelectorAll('[data-product-id]');
      })
      : document.querySelectorAll('[data-product-id]');

    const formsMapFromScan = buildFormsByProductIdMap(forms);

    perfDebug.log('_getDomId:nodeCount', { nodeCount: forms.length });

    const getDomIdChunkOpts = {
      chunkSize: GET_DOM_ID_SCAN_CHUNK,
      chunkTimeBudgetMs: 0,
      perfLabel: '_getDomId',
    };
    forEachInRafChunks(forms, getDomIdChunkOpts, function(form) {
      const {
        productId,
        productJson,
        withoutManyVariants,
        originalUpdatedAt,
      } = readProductFormDataset(form);

      if (originalUpdatedAt) {
        self.productsOriginalUpdatedAt.set(productId, originalUpdatedAt);
      }

      // Если есть json, то сразу инициализируем товар и сохраняем его
      const hasInlineProductJson = productId && productJson && typeof productJson === 'object' &&
        !productJson.bundle && Array.isArray(productJson.variants) && productJson.variants.length;

      if (hasInlineProductJson) {
        const productData = {};
        productData[productId] = productJson;
        idFromJSONSet.add(productId);

        perfDebug.log('_getDomId:inlineJson:enqueueSaveProducts', { productId });

        self._storage.saveProducts(productData, { checkLocale: true })
          .then(function() {
            const fe = formsMapFromScan[productId];
            const p = fe && fe.length
              ? self._initProduct(productJson, { formElements: fe })
              : self._initProduct(productJson);

            return Promise.resolve(p).then(function() {
              jquery(form).removeAttr('data-product-json');
            });
          })
          .catch(function() {
            if (!withoutManyVariants) {
              idSet.add(productId);
            }
          });
      } else if (productId) {
        // Пока в idSet меньше 10 id, геометрия не влияет на выбор ветки — не вызываем getBoundingClientRect
        let inViewport;
        if (idSet.size < 10) {
          inViewport = true;
        } else if (perfDebug.isEnabled()) {
          const t0 = perfDebug.now();
          inViewport = !!isOnScreen(form);
          perfDebug.recordIsOnScreen(perfDebug.now() - t0);
        } else {
          inViewport = isOnScreen(form);
        }
        if (inViewport && !withoutManyVariants) {
          idSet.add(productId);
        } else if (!withoutManyVariants) {
          idSetHidden.add(productId);
        }

        if (withoutManyVariants) {
          idSetWithoutManyVariants.add(productId);
        }
      }
    }).then(function() {
      perfDebug.timeAsyncEnd('_getDomId:finalize', function() {
        // Убираем пересечения с visible/hidden/json — иначе перезатрём в кеше товар уже загруженный с полным набором вариантов
        const onlyFirstVariantIds = [ ...idSetWithoutManyVariants ].filter(id => {
          if (!idSet.has(id) && !idSetHidden.has(id) && !idFromJSONSet.has(id)) {
            self.uniqueIdSetWithoutManyVariants.add(id);

            return true;
          }
        });

        result.resolve({
          visibleIds: [ ...idSet ],
          hiddenIds: [ ...idSetHidden ],
          onlyFirstVariantIds
        });
      });

      if (perfDebug.isEnabled()) {
        perfDebug.mark('_getDomId:scan:end');
        perfDebug.measureBetweenMarks(
          '_getDomId:scanWall',
          '_getDomId:scan:start',
          '_getDomId:scan:end',
        );
        perfDebug.log(
          '_getDomId:runDomIdScan:wallClock',
          { nodeCount: forms.length },
          perfDebug.now() - _perfScanStart,
        );
        perfDebug.logSummary();
      }
    });
  }

  // Нативный DCL вместо jQuery.ready: при readyState !== 'loading' jQuery вызывал колбэк синхронно
  // (в т.ч. внутри обработчика DOMContentLoaded при прогреве глобалов) и раздувал Script Evaluation / TBT.
  if (document.readyState === 'loading') {
    perfDebug.log('_getDomId:schedule', { type: 'DOMContentLoaded' });
    document.addEventListener('DOMContentLoaded', runDomIdScan, { once: true });
  } else {
    perfDebug.log('_getDomId:schedule', { type: 'setTimeout0' });
    setTimeout(runDomIdScan, 0);
  }

  if (perfDebug.isEnabled()) {
    perfDebug.log('_getDomId:syncUntilReturn', {}, perfDebug.now() - _perfGetDomIdSync);
  }

  return result.promise();
};

/**
 * @description
 * Получение информации о товаре
 *
 * @method
 * @private
 *
 * @param {Number} productId - id товара
 *
 * @return {jQuery.promise} промис с информацией по товару
 */
Products.prototype._getOne = function(productId, withoutManyVariants = false) {
  const self = this;
  const result = jquery.Deferred(); // eslint-disable-line new-cap

  if (productId === 0) {
    result.reject();

    return result.promise();
  }

  // а по сути нужно сделать все тоже, что и в
  // _getList, но забрать первый элемент
  self._enqueueProductFetch([ productId ], { withoutManyVariants })
    .done(function(products) {
      const productsArray = _toArray(products);

      result.resolve(productsArray[0]);
    })
    .fail(function() {
      result.reject();
    });

  return result.promise();
};

/**
 * @method
 * @private
 *
 * @description
 * Инициализация класса Product со всей хурмой про товар
 *
 * @param {Object} productJSON - json товара из стора
 * @param {{ formElements?: Element[] }} [initOpts] — узлы карточек этого id (без глобального селектора в Product)
 *
 * @return {void}
 */
Products.prototype._initProduct = function(productJSON, initOpts) {
  const self = this;

  return new Promise(function(resolve, reject) {
    if (!_isObject(productJSON)) {
      return reject();
    }
    try {
      const initOptsWithDefer = Object.assign({}, initOpts || {}, { deferInstanceInit: true });
      const t0 = perfDebug.isEnabled() ? perfDebug.now() : 0;
      const product = new Product(productJSON, self.settings, initOptsWithDefer);

      product._initInstanceAsync()
        .then(function() {
          self._products[productJSON.id] = product;
          if (perfDebug.isEnabled()) {
            const dt = perfDebug.now() - t0;
            const slowMs = typeof window !== 'undefined' && window.__INSALES_COMMON_PERF_SLOW_PRODUCT_MS__;
            const threshold = typeof slowMs === 'number' && slowMs > 0 ? slowMs : 30;
            if (dt >= threshold) {
              perfDebug.logWithStack(
                'products:newProduct:slow',
                {
                  productId: productJSON.id,
                  ms: Math.round(dt * 100) / 100,
                  threshold,
                  wallClockIncludesAsyncInstance: true,
                },
                22,
              );
            }
          }
          resolve(product);
        })
        .catch(reject);
    } catch (err) {
      console.warn('Проверьте разметку формы товара: ', err);
      reject();
    }
  });
};

/**
 * @method
 *
 * @description
 * Получаем экземпляр класса ProductInstance из ноды
 *
 * @param {jQuery.DOM} $node - узел, где хотим получть инстанс ProductInstance
 *
 * @return {Instance} инстанс ProductInstance
 */
Products.prototype.getInstance = function($node) {
  const self = this;
  const result = jquery.Deferred(); // eslint-disable-line new-cap

  const node = self._getInstanceNode($node);

  if (node.length) {
    const product = node[0].Product;

    if (product) {
      result.resolve(product);
    } else {
      self.initInstance($node)
        .done(function(initedProduct) {
          result.resolve(initedProduct);
        });
    }
  } else {
    console.warn(
      'Products.getInstance: Вы передали DOM-узел, в котором нет [data-product-id]'
    );
    result.reject();
  }

  return result.promise();
};

/**
 * @description
 * Инитим отдельную ноду. Важно! Является асинхронной функцией
 *
 * @param {jQuery.DOM} $node - узел, где хотим запустить ProductInstance
 *
 * @return {Instance} инстанс ProductInstance
 */
Products.prototype.initInstance = function($node) {
  const self = this;
  const node = self._getInstanceNode($node);
  const id = _toInteger(node.data('product-id'));

  const withoutManyVariants = Array.from(document.querySelectorAll(`[data-product-id="${id}"]`))
    .every(
      product => product.getAttribute('data-product-without-many-variants') === 'true'
    );

  return self._getOne(id, withoutManyVariants);
};

Products.prototype._bindDynamycProducts = function() {
  EventBus.subscribe('add_items:insales:cart:light', function() {
    setTimeout(function() {
      const newItems = jquery(document).find('[data-quantity]').not('[data-quantity-change-init]');
      if (newItems.length) {
        window.InSalesUI.bindUpdateCart(newItems);
      }
    }, 0);
  });
};

/**
 * @private
 *
 * @description
 * Хелпер поиска узла с проставленным [product-id]
 *
 * @param {jQuery.DOM} $node - узел, в котором хотим найти точку монтирования ProductInstance
 *
 * @return {DOMNode} искомый узел
 */
Products.prototype._getInstanceNode = function($node) {
  let node = $node;

  if (!node.data('product-id')) {
    node = node.find('[data-product-id]').first();
  }

  return node;
};

/**
 * @method
 * @private
 * @description
 * Собирает все элементы с [data-product-id] и возвращает объект: id товара → массив DOM-узлов (форм с одним id может быть несколько).
 *
 * @return {Object.<number, Array<Element>>} карта productId → массив элементов
 */
Products.prototype._getFormsByProductId = function() {
  const self = this;
  const nodes = document.querySelectorAll('[data-product-id]');
  const len = nodes.length;
  const formsCache = self._formsByProductIdCache;
  if (
    formsCache &&
    formsCache.nodeCount === len &&
    len > 0 &&
    formsCache.firstNode === nodes[0] &&
    formsCache.lastNode === nodes[len - 1]
  ) {
    perfDebug.log('products:_getFormsByProductId:cacheHit', { nodeCount: len });

    return formsCache.map;
  }

  const map = buildFormsByProductIdMap(nodes);

  if (len > 0) {
    self._formsByProductIdCache = {
      map,
      nodeCount: len,
      firstNode: nodes[0],
      lastNode: nodes[len - 1],
    };
  }

  return map;
};

/**
 * Плейсхолдер в _products без `new Product`: ждём полноценный инит, но батчи getList уже могут резолвиться.
 * (Сырой JSON вместо экземпляра Product, пока в DOM нужны все варианты.)
 *
 * @param {Object} product — JSON из стора
 * @param {Object.<number, Element[]>} formsByProductId
 * @return {boolean} true если зарегистрирован плейсхолдер (и _initProduct вызывать не нужно)
 * @private
 */
Products.prototype._registerMultiVariantPlaceholder = function(product, formsByProductId) {
  const self = this;

  if (!product.is_more_than_one_variant) {
    return false;
  }

  const forms = formsByProductId[product.id] || [];
  const onlyFirstVariantInDom = forms.every(
    node => node.getAttribute('data-product-without-many-variants') === 'true'
  );

  if (onlyFirstVariantInDom) {
    return false;
  }

  // Кладём сырой json: так _pickInitedProducts отдаёт данные, и промисы getList успевают резолвиться до полного Product-инита
  self._products[product.id] = product;

  return true;
};

/**
 * Создаёт экземпляры Product (или плейсхолдер см. _registerMultiVariantPlaceholder) по данным из стора после getProducts.
 * Обход разбит на чанки с requestAnimationFrame между ними, чтобы не блокировать главный поток.
 *
 * @param {Object[]} products
 * @return {Promise<void>}
 */
Products.prototype._initStoreProducts = function(products) {
  const self = this;
  // getProducts отдаёт объект id → json; раньше обходили через lodash forEach по значениям
  const productList = _isArray(products) ? products : Object.values(products || {});
  const formsByProductId = perfDebug.isEnabled()
    ? perfDebug.timeSync('products:_getFormsByProductId', function() {
      return self._getFormsByProductId();
    })
    : self._getFormsByProductId();

  warmupTemplatesIfNeeded();

  perfDebug.log('products:_initStoreProducts', {
    products: productList.length,
    chunkSize: PRODUCTS_INIT_CHUNK,
    firstBatchSize: Math.min(PRODUCTS_INIT_FIRST_BATCH_SIZE, productList.length),
  });

  function initOneStoreProduct(product) {
    if (!self._registerMultiVariantPlaceholder(product, formsByProductId)) {
      const existing = self._products[product.id];
      if (existing && typeof existing.getProduct === 'function') {
        perfDebug.log('products:_initProduct:skipDuplicate', { productId: product.id });

        return;
      }
      const formEls = formsByProductId[product.id];
      if (formEls && formEls.length) {
        return self._initProduct(product, { formElements: formEls });
      }

      return self._initProduct(product);
    }
  }

  const firstN = Math.min(PRODUCTS_INIT_FIRST_BATCH_SIZE, productList.length);
  const firstBatch = productList.slice(0, firstN);
  const restBatch = productList.slice(firstN);

  return forEachInRafChunks(
    firstBatch,
    {
      chunkSize: 1,
      chunkTimeBudgetMs: PRODUCTS_INIT_CHUNK_TIME_FIRST_MS,
      perfLabel: '_initStoreProducts',
    },
    initOneStoreProduct,
  ).then(function() {
    return forEachInRafChunks(
      restBatch,
      {
        chunkSize: PRODUCTS_INIT_CHUNK,
        chunkTimeBudgetMs: 25,
        perfLabel: '_initStoreProducts',
      },
      initOneStoreProduct,
    );
  });
};

// если чего-то нет в self._products от idList, то забираем данные от них
Products.prototype._pickInitedProducts = function(productIds) {
  const self = this;

  const productIdsList = _compact(_concat(productIds, self.productIds));

  return _reduce(_pick(self._products, productIdsList), function(result, product) {
    result[product.id] = product.getProduct ? product.getProduct() : product;

    return result;
  }, {});
};

/**
 * @private
 * @method
 * @description
 * Вспомогательный метод, проверяет, все ли запрашиваемые товары были получены
 * @param {Array} testList - массив с id запрашиваемых товаров
 * @param {Array} currentList - массив с id полученных товаров
 * @return {Boolean} результат проверки
 */
Products.prototype.testIds = function(testList, currentList) {
  const self = this;
  const withoutDeleted = _difference(testList, self.deleted);
  const difference = _difference(withoutDeleted, currentList);

  return _size(difference) === 0;
};

export { Products };
export default deferredSingletonExport(_Singleton(Products), Products);

perfDebug.logModuleEval('@/products/index');
